import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ApiError } from "./lib/api";
import { AuthProvider, useAuth } from "./lib/auth";
import { FiltersProvider } from "./lib/filters";
import { canAccessPath, homeFor } from "./lib/access";
import { GovernancePage } from "./pages/GovernancePage";

// Layout
import { AppShell } from "./components/AppShell";
// Portal Operacional de Frota e Manutenção — mesma aplicação, casca mobile-first
import { PortalShell } from "./features/portal/PortalShell";
import { MaintenanceMobileShell } from "./features/maintenance-mobile/MaintenanceMobileShell";
import { useMobileLayout } from "./features/maintenance-mobile/useMobileLayout";
import { PortalHomePage } from "./pages/portal/PortalHomePage";
import { PortalChecklistsPage } from "./pages/portal/PortalChecklistsPage";
import { PortalStartChecklistPage } from "./pages/portal/PortalStartChecklistPage";
import { PortalOccurrencesPage } from "./pages/portal/PortalOccurrencesPage";
import { PortalMaintenancePage } from "./pages/portal/PortalMaintenancePage";
import { PortalTiresPage } from "./pages/portal/PortalTiresPage";
import { PortalTireInspectionPage } from "./pages/portal/PortalTireInspectionPage";
import { PortalVehiclesPage } from "./pages/portal/PortalVehiclesPage";
import { PortalVehicleHistoryPage } from "./pages/portal/PortalVehicleHistoryPage";

// Legacy pages (preserved, accessible via Settings)
import { LoginPage }             from "./pages/LoginPage";
import { DataSourcesPage }       from "./pages/DataSourcesPage";
import { MappingsPage }          from "./pages/MappingsPage";
import { WhatsappInstancesPage } from "./pages/WhatsappInstancesPage";

// New pages
import { CalculationMemoryPage } from "./components/calculation-memory/CalculationMemoryPage";
import { FleetMemoryPage } from "./components/card-memory/FleetMemoryPage";
import { ExecutivePage }    from "./pages/ExecutivePage";
import { FinancialPage }    from "./pages/FinancialPage";
import { OperationalPage }  from "./pages/OperationalPage";
import { FuelPage }         from "./pages/FuelPage";
import { TiresPage }        from "./pages/TiresPage";
import { TireDetailPage }   from "./pages/TireDetailPage";
import { MaintenancePage }  from "./pages/MaintenancePage";
import { MaintenanceHubPage } from "./pages/MaintenanceHubPage";
import { MaintenanceCentralPage } from "./pages/MaintenanceCentralPage";
import { MaintenancePlatePage } from "./pages/MaintenancePlatePage";
import { MaintenanceQuickPage } from "./pages/MaintenanceQuickPage";
import { MaintenanceChecklistsPage } from "./pages/MaintenanceChecklistsPage";
import { MaintenanceRunPage } from "./pages/MaintenanceRunPage";
import { MaintenanceWorkOrdersPage } from "./pages/MaintenanceWorkOrdersPage";
import { MaintenanceErpOrdersPage } from "./pages/MaintenanceErpOrdersPage";
import { MaintenanceErpOrderPage } from "./pages/MaintenanceErpOrderPage";
import { MaintenanceWorkOrderPage } from "./pages/MaintenanceWorkOrderPage";
import { MaintenancePreventivePage } from "./pages/MaintenancePreventivePage";
import { MaintenanceTemplatesPage } from "./pages/MaintenanceTemplatesPage";
import { MaintenanceTemplateEditorPage } from "./pages/MaintenanceTemplateEditorPage";
import { MaintenanceWorkOrderPrintPage } from "./pages/MaintenanceWorkOrderPrintPage";
import { MaintenanceVehicleGroupsPage } from "./pages/MaintenanceVehicleGroupsPage";
import { MaintenanceVehicleGroupEditorPage } from "./pages/MaintenanceVehicleGroupEditorPage";
import { MaintenanceNonConformitiesPage } from "./pages/MaintenanceNonConformitiesPage";
import { FleetOverviewPage } from "./pages/FleetOverviewPage";
import { MarginByPlatePage } from "./pages/MarginByPlatePage";
import { VehicleDrilldownPage } from "./pages/VehicleDrilldownPage";
import TollImportPage from "./pages/TollImportPage";
import { VehicleCostPage } from "./pages/VehicleCostPage";
import { InsurancePage } from "./pages/InsurancePage";
import { DriversPage } from "./pages/DriversPage";
import { DREPage }          from "./pages/DREPage";
import { CashflowPage }     from "./pages/CashflowPage";
import { IntelligencePage } from "./pages/IntelligencePage";
import { WhatsAppPage }     from "./pages/WhatsAppPage";
import { DataQualityPage }  from "./pages/DataQualityPage";
import { SyncPage }         from "./pages/SyncPage";
import { SettingsPage }     from "./pages/SettingsPage";

// Legacy (kept for backward compatibility — also accessible via /whatsapp)
import { LogsPage }  from "./pages/LogsPage";
import { AskPage }   from "./pages/AskPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Erro 4xx (sem permissão, não encontrado, parâmetro inválido) não melhora
      // tentando de novo: só atrasa o aviso e, no 403, ainda dispara a releitura
      // da sessão. Só falha de rede/servidor ganha a segunda tentativa.
      retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FiltersProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              {/* Portal Operacional — mesma autenticação, casca própria.
                  Fora do AppShell de propósito: aquele tem sidebar fixa de 256 px. */}
              {/* Impressão da O.S.: folha A4, sem menu. */}
              <Route path="/frota/manutencao/ordens/:id/imprimir" element={<Protected><MaintenanceWorkOrderPrintPage /></Protected>} />

              <Route path="/app/frota" element={<Protected><PortalShell /></Protected>}>
                <Route index                    element={<PortalHomePage />} />
                <Route path="checklists"        element={<PortalChecklistsPage />} />
                <Route path="checklists/novo"   element={<PortalStartChecklistPage />} />
                <Route path="checklists/:id"    element={<MaintenanceRunPage variant="portal" />} />
                <Route path="ordens/:id"        element={<MaintenanceWorkOrderPage variant="portal" />} />
                <Route path="ocorrencias"       element={<PortalOccurrencesPage />} />
                <Route path="manutencao"        element={<PortalMaintenancePage />} />
                <Route path="pneus"             element={<PortalTiresPage />} />
                <Route path="pneus/inspecao"    element={<PortalTireInspectionPage />} />
                <Route path="veiculos"          element={<PortalVehiclesPage />} />
                <Route path="veiculos/:plate"   element={<PortalVehicleHistoryPage />} />
                <Route path="memoria/:cardKey"  element={<FleetMemoryPage />} />
              </Route>

              {/* Perfil Manutenção: no celular/tablet a moldura é a casca mobile; as rotas são as mesmas. */}
              <Route element={<Protected><ShellDoAparelho /></Protected>}>
                {/* Main dashboards */}
                <Route index                  element={<ExecutivePage />}   />
                <Route path="financeiro"      element={<FinancialPage />}   />
                <Route path="financeiro/memoria/:cardKey" element={<CalculationMemoryPage />} />
                <Route path="operacional"     element={<OperationalPage />} />
                <Route path="operacional/memoria/:cardKey" element={<CalculationMemoryPage />} />
                <Route path="dre"             element={<DREPage />}         />
                <Route path="fluxo-caixa"     element={<CashflowPage />}    />

                {/* Frota */}
                <Route path="frota"             element={<FleetOverviewPage />} />
                <Route path="frota/memoria/:cardKey" element={<FleetMemoryPage />} />
                <Route path="frota/combustivel" element={<FuelPage />} />
                <Route path="frota/pneus"       element={<TiresPage />} />
                <Route path="frota/pneus/:id"   element={<TireDetailPage />} />
                {/* /frota/manutencao continua sendo o BI de CUSTO vindo do ERP.
                    O módulo operacional (checklist → não conformidade → O.S.)
                    vive nas rotas filhas — são coisas diferentes e a tela antiga
                    não foi absorvida. */}
                <Route path="frota/manutencao"  element={<MaintenanceCentralPage />} />
                <Route path="frota/manutencao/registrar" element={<MaintenanceHubPage />} />
                <Route path="frota/manutencao/custos" element={<MaintenancePage />} />
                <Route path="frota/manutencao/nova"   element={<MaintenanceQuickPage />} />
                <Route path="frota/manutencao/placa/:plate" element={<MaintenancePlatePage />} />
                <Route path="frota/manutencao/checklists"     element={<MaintenanceChecklistsPage />} />
                <Route path="frota/manutencao/checklists/:id" element={<MaintenanceRunPage />} />
                <Route path="frota/manutencao/nao-conformidades" element={<MaintenanceNonConformitiesPage />} />
                <Route path="frota/manutencao/ordens"     element={<MaintenanceWorkOrdersPage />} />
                <Route path="frota/manutencao/ordens/:id" element={<MaintenanceWorkOrderPage />} />
                <Route path="frota/manutencao/erp"        element={<MaintenanceErpOrdersPage />} />
                <Route path="frota/manutencao/erp/:id"    element={<MaintenanceErpOrderPage />} />
                <Route path="frota/manutencao/preventiva" element={<MaintenancePreventivePage />} />
                <Route path="frota/manutencao/gestao"     element={<Navigate to="/frota/manutencao" replace />} />
                <Route path="frota/manutencao/modelos"     element={<MaintenanceTemplatesPage />} />
                <Route path="frota/manutencao/grupos"      element={<MaintenanceVehicleGroupsPage />} />
                <Route path="frota/manutencao/grupos/:id"  element={<MaintenanceVehicleGroupEditorPage />} />
                <Route path="frota/manutencao/modelos/:id" element={<MaintenanceTemplateEditorPage />} />
                <Route path="frota/custo-placa" element={<VehicleCostPage />} />
                <Route path="frota/margem"      element={<MarginByPlatePage />} />
                <Route path="frota/placa/:plate" element={<VehicleDrilldownPage />} />
                <Route path="frota/pedagio"     element={<TollImportPage />} />
                <Route path="frota/seguro"      element={<InsurancePage />} />
                <Route path="frota/motoristas"  element={<DriversPage />} />

                {/* Custos foi incorporado como aba dentro da DRE / Resultado */}
                <Route path="custos"          element={<Navigate to="/dre?tab=custos" replace />} />

                {/* Intelligence — Insights e Alertas unificados */}
                <Route path="inteligencia"    element={<IntelligencePage />} />
                {/* Rotas antigas preservadas → redirecionam para a tela unificada */}
                <Route path="insights"        element={<Navigate to="/inteligencia" replace />} />
                <Route path="alertas"         element={<Navigate to="/inteligencia" replace />} />
                {/* Relatórios removido — era estático; volta quando houver geração real */}
                <Route path="relatorios"      element={<Navigate to="/inteligencia" replace />} />

                {/* Channel */}
                <Route path="whatsapp"        element={<WhatsAppPage />}    />

                {/* System */}
                <Route path="qualidade-dados" element={<DataQualityPage />} />
                <Route path="sincronizacao"   element={<SyncPage />}        />
                <Route path="configuracoes"   element={<SettingsPage />}    />
                <Route path="governanca" element={<GovernancePage />} />

                {/* Legacy routes (preserved) */}
                <Route path="perguntas"                            element={<AskPage />}             />
                <Route path="logs"                                 element={<LogsPage />}            />
                <Route path="data-sources"                         element={<DataSourcesPage />}     />
                <Route path="data-sources/:id/mappings"            element={<MappingsPage />}        />
                <Route path="whatsapp-instances"                   element={<WhatsappInstancesPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </FiltersProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

/** Menu lateral (computador) ou casca mobile (perfil Manutenção no celular/tablet). */
function ShellDoAparelho() {
  const { layout } = useMobileLayout();
  return layout === "mobile" ? <MaintenanceMobileShell /> : <AppShell />;
}

function Protected({ children }: { children: React.ReactNode }) {
  const { user, ready } = useAuth();
  const { pathname } = useLocation();
  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-surface-muted">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-tm-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Carregando…</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessPath(user, pathname)) return <Navigate to={homeFor(user)} replace />;
  return <>{children}</>;
}
